import { forwardRef, useEffect, useImperativeHandle, useRef, useState, useMemo, type ReactNode } from 'react'
import {
  Box,
  Table,
  Tbody,
  Tr,
  Td,
  Spinner,
  Text,
  VStack,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from '@chakra-ui/react'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { TableHeader, type TableHeaderRef } from './TableHeader'
import { TableBody, type TableBodyRef } from './TableBody'
import { Pagination } from './Pagination'
import type { DataTableOptions, Permission, SortParams, MetaData } from './types'
import { useThemeColors } from '@/hooks/useThemeColors'
import { tableStyles } from './tableStyles'

interface DataTableProps {
  options: DataTableOptions
  params?: Record<string, any>
  onOpenModal?: (id: any) => void
  onOpenItem?: (id: any) => void
  setSubSub?: (all: number, trashed: number) => void
  access?: Permission
  filterOptions?: Record<string, Array<{ value: string | number; label: string }>>
  customRenderers?: Record<string, (value: any, row?: any) => ReactNode>
  showCheckbox?: boolean
  itemClickable?: boolean
  setTotal?: (total: number) => void
  editable?: boolean
  hideActions?: boolean
}

export interface DataTableRef {
  getSelectedRows: () => number[]
  clearPage: () => void
  reload: () => void
  applyFilter: (field: string, value: any) => void
}

export const DataTable = forwardRef<DataTableRef, DataTableProps>(
  ({ options, params, onOpenModal, onOpenItem, setSubSub, access, filterOptions, customRenderers, showCheckbox = true, itemClickable = false, setTotal, editable = true, hideActions = false }, ref) => {
    const { showError, showSuccess } = useToastNotification()
    const [sortParams, setSortParams] = useState<SortParams>({ order: '', sort: 'asc' })
    const [isLoading, setIsLoading] = useState(false)
    const [dataRows, setDataRows] = useState<any[]>([])
    const [totalRows, setTotalRows] = useState<number | null>(null)
    const [metaData, setMetaData] = useState<MetaData | null>(null)
    const [nextPage, setNextPage] = useState<string | null>(null)
    const [toDelete, setToDelete] = useState<any>(null)
    const [toRestore, setToRestore] = useState<any>(null)
    const [filters, setFilters] = useState<Record<string, any>>({})

    const theadRef = useRef<TableHeaderRef>(null)
    const tbodyRef = useRef<TableBodyRef>(null)
    const { isOpen, onOpen, onClose } = useDisclosure()
    const [modalDescription, setModalDescription] = useState('')

    const colors = useThemeColors()

    const mergedDataFields = useMemo(() => {
      if (!filterOptions) return options.dataFields

      const merged = { ...options.dataFields }
      Object.keys(filterOptions).forEach((field) => {
        if (merged[field]) {
          merged[field] = {
            ...merged[field],
            withFilter: true,
            filterOptions: filterOptions[field],
          }
        }
      })
      return merged
    }, [options.dataFields, filterOptions])

    const handleSort = (field: string, sort: 'asc' | 'desc') => {
      setSortParams({ order: field, sort })
    }

    const handleFilter = (field: string, value: any) => {
      setFilters((prev) => {
        if (value === null || value === undefined || value === '') {
          const newFilters = { ...prev }
          delete newFilters[field]
          return newFilters
        }
        return { ...prev, [field]: value }
      })
      setNextPage(null)
    }

    const handleAction = (id: any, action: string) => {
      setToRestore(null)
      setToDelete(null)

      if (action === 'Edit' && options?.displayInModal) {
        onOpenModal?.(id)
        return
      }

      const actionMessages: Record<string, string> = {
        Restore: 'Do you really want to restore this record?',
        Trash: 'Do you really want to delete this record?',
        Delete: 'You are about to permanently delete these items from your site. This action cannot be undone.',
        BulkDelete: 'You are about to permanently delete multiple items from your site. This action cannot be undone.',
        BulkRestore: 'Do you really want to restore multiple records?',
      }

      if (actionMessages[action]) {
        setToRestore(action === 'Restore' || action === 'BulkRestore' ? id : null)
        setToDelete(action === 'Trash' || action === 'Delete' || action === 'BulkDelete' ? id : null)
        setModalDescription(actionMessages[action])
        onOpen()
      }
    }

    const onValidate = async () => {
      try {
        if (toDelete) {
          const url = `${options.dataSource}/${toDelete}`
          await apiClient.delete(url)
          showSuccess('Success', 'Record deleted successfully')
          setNextPage(null)
          getDataSource()
        }

        if (toRestore) {
          const url = `${options.dataSource}/restore/${toRestore}`
          await apiClient.patch(url)
          showSuccess('Success', 'Record restored successfully')
          setNextPage(null)
          getDataSource()
        }
      } catch (error: any) {
        showError(error)
      } finally {
        onClose()
        setToDelete(null)
        setToRestore(null)
      }
    }

    const getDataSource = async () => {
      setIsLoading(true)
      try {
        const dataSource = nextPage || options.dataSource
        const requestParams = { ...params, ...sortParams, ...filters }
        const { data } = await apiClient.get(dataSource, { params: requestParams })

        if (data.data) {
          const meta = data.meta || {
            total: data.total ?? 0,
            per_page: data.per_page ?? data.data.length,
            current_page: data.current_page ?? 1,
            last_page: data.last_page ?? 1,
            from: data.from ?? null,
            to: data.to ?? null,
            links: data.links ?? [],
            path: data.path ?? '',
            first_page_url: data.first_page_url ?? null,
            last_page_url: data.last_page_url ?? null,
            next_page_url: data.next_page_url ?? null,
            prev_page_url: data.prev_page_url ?? null,
          }

          setTotalRows(meta.total ?? 0)
          setTotal?.(meta.all ?? 0)
          if (options.softDelete) {
            setSubSub?.(meta.all || 0, meta.trashed || 0)
          }
          setDataRows(data.data)
          setMetaData(meta)
        } else {
          setTotalRows(data.length)
          setTotal?.(data.length)
          setDataRows(data)
          setMetaData({
            total: data.length,
            per_page: data.length,
            current_page: 1,
            last_page: 1,
            links: [],
          } as MetaData)
        }

        theadRef.current?.setCheckedAll(false)
      } catch (error: any) {
        showError(error)
      } finally {
        setIsLoading(false)
      }
    }

    useEffect(() => {
      const timeoutId = setTimeout(() => {
        getDataSource()
      }, 300)
      return () => clearTimeout(timeoutId)
    }, [params, options.dataSource, sortParams, nextPage, filters])

    useEffect(() => {
      setNextPage(null)
    }, [params, filters])

    useImperativeHandle(ref, () => ({
      getSelectedRows() {
        return tbodyRef.current?.getSelectedData() || []
      },
      clearPage() {
        setNextPage(null)
      },
      reload() {
        setDataRows([])
        getDataSource()
        theadRef.current?.setCheckedAll(false)
      },
      applyFilter(field: string, value: any) {
        handleFilter(field, value)
      },
    }))

    const handlePageChange = (url: string | null) => {
      if (url) {
        setNextPage(url)
      }
    }

    const handlePerPageChange = (newPerPage: number) => {
      // Update params to include the new per_page value
      // This will trigger a re-fetch with the new per_page parameter
      const newParams = { ...params, per_page: newPerPage }
      
      // Reset to page 1 when changing per_page
      const baseUrl = metaData?.path || options.dataSource
      setNextPage(`${baseUrl}?per_page=${newPerPage}&page=1`)
    }

    const renderTableContent = () => {
      if (isLoading) {
        return (
          <Tbody>
            <Tr>
              <Td colSpan={Object.keys(options.dataFields).length + 2} textAlign="center" py={8}>
                <VStack spacing={2}>
                  <Spinner {...tableStyles.loading.spinner} />
                  <Text color={colors.text} {...tableStyles.loading.text}>Loading...</Text>
                </VStack>
              </Td>
            </Tr>
          </Tbody>
        )
      }

      if (totalRows === 0 || dataRows.length === 0) {
        return (
          <Tbody>
            <Tr>
              <Td colSpan={Object.keys(options.dataFields).length + 2} textAlign="center" py={8}>
                <Text color={colors.textSecondary} {...tableStyles.empty.text}>
                  {options.noRecordText || 'No Record Found'}
                </Text>
              </Td>
            </Tr>
          </Tbody>
        )
      }

      return (
        <TableBody
          options={options}
          rows={dataRows}
          ref={tbodyRef}
          permissions={access}
          onAction={handleAction}
          onCheckedAll={(checked) => theadRef.current?.setCheckedAll(checked)}
          customRenderers={customRenderers}
          showCheckbox={showCheckbox}
          onOpenItem={onOpenItem}
          itemClickable={itemClickable}
          editable={editable}
          hideActions={hideActions}
        />
      )
    }

    return (
      <Box display="flex" flexDirection="column" h="100%" minH={0} position="relative" {...tableStyles.container}>
        <Box
          flex="1"
          minH={0}
          overflowX="auto"
          overflowY="auto"
          position="relative"
          css={{
            ...tableStyles.scrollbar,
            thead: tableStyles.thead,
            'thead th': tableStyles.theadCell,
            'tbody td': tableStyles.tbodyCell,
            'tbody tr': tableStyles.tbodyRow,
            'tbody tr:hover': tableStyles.tbodyRowHover,
          }}
        >
          <Table variant="simple" size="md" w="100%">
            <TableHeader
              header={mergedDataFields}
              onCheckAll={(checked) => tbodyRef.current?.checkedAll(checked)}
              onSort={handleSort}
              onFilter={handleFilter}
              activeFilters={filters}
              ref={theadRef}
              showActionsColumn={true}
              showCheckbox={showCheckbox}
              hideActionsLabel={true}
            />
            {renderTableContent()}
          </Table>
        </Box>

        {metaData && metaData.total > 0 && metaData.links && metaData.links.length > 0 && (
          <Box {...tableStyles.pagination.container}>
            <Pagination
              metas={metaData}
              onClick={handlePageChange}
              onPerPageChange={handlePerPageChange}
            />
          </Box>
        )}

        <Modal isOpen={isOpen} onClose={onClose} isCentered >
          <ModalOverlay  />
          <ModalContent {...tableStyles.modal.content}>
            <ModalHeader {...tableStyles.modal.header}>Confirmation</ModalHeader>
            <ModalBody>
              <Text {...tableStyles.modal.body}>{modalDescription}</Text>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose} {...tableStyles.modal.button} {...tableStyles.modal.cancelButton}>
                Cancel
              </Button>
              <Button colorScheme="red"  onClick={onValidate} {...tableStyles.modal.button}>
                Confirm
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    )
  }
)

DataTable.displayName = 'DataTable'


